<template>
  <div class="bg-white rounded-lg shadow-sm text-gray mx-2 my-3 p-4 border border-gray-200">
    <!-- Log Details Modal -->
    <AppModal
      v-model="showDetailsModal"
      title="Activity Log Details"
      size="lg"
      :hideFooter="true"
    >
      <div v-if="selectedLog" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded-lg border border-gray-100">
          <div class="space-y-2">
            <p><span class="font-semibold text-gray-500 mr-2">ID:</span> <span class="text-gray-900 font-mono">{{ selectedLog.id }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">Service:</span> <span class="text-gray-900 capitalize">{{ selectedLog.service }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">User ID:</span> <span class="text-gray-900">{{ selectedLog.userId || '—' }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">IP Address:</span> <span class="text-gray-900 font-mono">{{ selectedLog.ip || '—' }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">Method:</span> <span class="font-bold text-primary-600">{{ selectedLog.method }}</span></p>
            <p>
              <span class="font-semibold text-gray-500 mr-2">Status Code:</span>
              <span :class="statusCodeClass(selectedLog.statusCode)" class="px-2 py-0.5 rounded font-mono text-xs font-bold bg-white shadow-sm border border-gray-200">
                {{ selectedLog.statusCode }}
              </span>
            </p>
          </div>
          <div class="space-y-2">
            <p class="break-all"><span class="font-semibold text-gray-500 mr-2">URL:</span> <span class="text-gray-900 font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-gray-200">{{ selectedLog.url }}</span></p>
            <p v-if="selectedLog.queryString" class="break-all"><span class="font-semibold text-gray-500 mr-2">Query String:</span> <span class="text-gray-900 font-mono text-xs">{{ selectedLog.queryString }}</span></p>
            <p v-if="selectedLog.referer"><span class="font-semibold text-gray-500 mr-2">Referer:</span> <span class="text-gray-900 text-xs">{{ selectedLog.referer }}</span></p>
            <p v-if="selectedLog.sessionId"><span class="font-semibold text-gray-500 mr-2">Session ID:</span> <span class="text-gray-900 font-mono text-xs">{{ selectedLog.sessionId }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">Start Time:</span> <span class="text-gray-900 font-mono text-xs">{{ dateFormat(selectedLog.startTimestamp) }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">End Time:</span> <span class="text-gray-900 font-mono text-xs">{{ dateFormat(selectedLog.endTimestamp) }}</span></p>
            <p><span class="font-semibold text-gray-500 mr-2">Created At:</span> <span class="text-gray-900 font-mono text-xs">{{ dateFormat(selectedLog.createdAt) }}</span></p>
          </div>
        </div>

        <div v-if="selectedLog.userAgent" class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h4 class="font-semibold text-gray-600 mb-1 text-xs uppercase tracking-wider">User Agent</h4>
          <p class="text-gray-800 text-xs font-mono break-all">{{ selectedLog.userAgent }}</p>
        </div>

        <div v-if="selectedLog.headers" class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h4 class="font-semibold text-gray-600 mb-2 text-xs uppercase tracking-wider">Headers</h4>
          <pre class="bg-white p-3 rounded border border-gray-200 text-xs font-mono overflow-x-auto max-h-40 whitespace-pre-wrap break-all text-gray-800">{{ selectedLog.headers }}</pre>
        </div>

        <div v-if="selectedLog.request" class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h4 class="font-semibold text-gray-600 mb-2 text-xs uppercase tracking-wider">Request</h4>
          <pre class="bg-white p-3 rounded border border-gray-200 text-xs font-mono overflow-x-auto max-h-60 whitespace-pre-wrap break-all text-gray-800">{{ selectedLog.request }}</pre>
        </div>

        <div v-if="selectedLog.response" class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h4 class="font-semibold text-gray-600 mb-2 text-xs uppercase tracking-wider">Response</h4>
          <pre class="bg-white p-3 rounded border border-gray-200 text-xs font-mono overflow-x-auto max-h-60 whitespace-pre-wrap break-all text-gray-800">{{ selectedLog.response }}</pre>
        </div>
      </div>
    </AppModal>

    <!-- Title Header -->
    <div class="mb-6 flex justify-between items-center border-b pb-3 border-gray-100">
      <h1 class="text-xl font-bold text-gray-900 flex items-center gap-2">
        <i class="fas fa-list-alt text-primary"></i> Activity Logs
      </h1>
    </div>

    <!-- Filters Section -->
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 mb-4">
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">Year</label>
        <AppSelect
          v-model="filters.year"
          :options="yearOptions"
          @change="handleSearch"
        />
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">Month</label>
        <AppSelect
          v-model="filters.month"
          :options="monthOptions"
          @change="handleSearch"
        />
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">Service</label>
        <AppSelect
          v-model="filters.service"
          :options="serviceOptions"
          @change="handleSearch"
        />
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">Method</label>
        <AppSelect
          v-model="filters.method"
          :options="methodOptions"
          @change="handleSearch"
        />
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">Status Code</label>
        <AppSelect
          v-model="filters.statusCode"
          :options="statusCodeOptions"
          @change="handleSearch"
        />
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">User ID</label>
        <AppInput
          v-model="filters.userId"
          placeholder="Filter by user"
          @keyup.enter="handleSearch"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6 items-end">
      <div>
        <div class="flex justify-between items-center mb-1">
          <label class="block text-xs font-bold text-gray-500">Search</label>
          <label class="inline-flex items-center gap-1.5 cursor-pointer text-xs font-medium text-gray-600 select-none">
            <input
              type="checkbox"
              v-model="filters.matchPhrase"
              class="w-3.5 h-3.5 text-primary border-gray-300 rounded focus:ring-primary focus:ring-1 cursor-pointer"
              @change="handleSearch"
            />
            <span>Exact phrase</span>
          </label>
        </div>
        <div class="relative">
          <AppInput
            v-model="filters.search"
            placeholder="Search logs..."
            :inputClass="filters.search ? 'pr-8' : ''"
            @keyup.enter="handleSearch"
          />
          <button
            v-if="filters.search"
            type="button"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            title="Clear search"
            @click="clearSearch"
          >
            <i class="fa fa-times-circle text-sm"></i>
          </button>
        </div>
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">Start Date</label>
        <AppInput
          v-model="filters.startDateLocal"
          type="datetime-local"
        />
      </div>
      <div>
        <label class="block text-xs font-bold text-gray-500 mb-1">End Date</label>
        <AppInput
          v-model="filters.endDateLocal"
          type="datetime-local"
        />
      </div>
      <div class="flex gap-2">
        <button
          class="btn-primary flex-1 flex justify-center items-center"
          :disabled="loading"
          @click="handleSearch"
        >
          <i class="fa fa-search mr-2"></i> Search
        </button>
        <button
          class="btn-secondary flex-1 flex justify-center items-center"
          :disabled="exporting || loading"
          @click="exportLogs"
        >
          <i v-if="exporting" class="fa fa-spinner fa-spin mr-2"></i>
          <i v-else class="fa fa-download mr-2"></i>
          {{ exporting ? "Exporting..." : "Export" }}
        </button>
      </div>
    </div>

    <!-- Spinner Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <AppSpinner size="lg" />
    </div>

    <!-- Table Section -->
    <div v-else class="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">#</th>
            <th class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Timestamp</th>
            <th class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Service</th>
            <th class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Method</th>
            <th class="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
            <th class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User ID</th>
            <th class="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">URL</th>
            <th class="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr
            v-for="(log, idx) in logs"
            :key="log.id || idx"
            class="hover:bg-gray-50 transition-colors"
          >
            <td class="px-4 py-3 text-xs font-medium text-gray-900">
              {{ (currentPage - 1) * size + idx + 1 }}
            </td>
            <td class="px-4 py-3 text-xs text-gray-600 font-mono whitespace-nowrap">
              {{ dateFormat(log.createdAt || log.startTimestamp) }}
            </td>
            <td class="px-4 py-3 text-xs text-gray-900 capitalize font-medium whitespace-nowrap">
              {{ log.service || "—" }}
            </td>
            <td class="px-4 py-3 text-xs text-gray-700 font-semibold whitespace-nowrap">
              {{ log.method || "—" }}
            </td>
            <td class="px-4 py-3 text-xs text-center whitespace-nowrap">
              <span :class="statusCodeClass(log.statusCode)" class="px-2 py-0.5 rounded font-mono text-xs font-bold bg-gray-50 border border-gray-200">
                {{ log.statusCode || "—" }}
              </span>
            </td>
            <td class="px-4 py-3 text-xs text-gray-500 truncate max-w-[120px]" :title="log.userId">
              {{ log.userId || "—" }}
            </td>
            <td class="px-4 py-3 text-xs text-gray-500 font-mono truncate max-w-[200px]" :title="log.url">
              {{ log.url || "—" }}
            </td>
            <td class="px-4 py-3 text-xs text-center whitespace-nowrap">
              <button
                class="btn-sm-primary flex items-center gap-1 mx-auto"
                @click="viewDetails(log)"
              >
                <i class="fa fa-eye"></i> View
              </button>
            </td>
          </tr>
          <tr v-if="logs.length === 0">
            <td colspan="8" class="text-center py-8 text-gray-400 text-sm font-medium bg-gray-50">
              No results found
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Section -->
    <div v-if="pagination.totalPages > 1" class="flex flex-col sm:flex-row items-center justify-between mt-4 px-1 gap-4">
      <div class="flex items-center gap-3">
        <button
          @click="previous"
          :class="currentPage > 1 ? 'btn-sm-primary' : 'btn-sm-primary opacity-50 cursor-not-allowed'"
          :disabled="currentPage === 1"
        >
          Previous
        </button>
        <span class="text-xs font-semibold text-gray-700">
          Page {{ currentPage }} of {{ pagination.totalPages }}
        </span>
        <button
          @click="next"
          :class="currentPage < pagination.totalPages ? 'btn-sm-primary' : 'btn-sm-primary opacity-50 cursor-not-allowed'"
          :disabled="currentPage === pagination.totalPages"
        >
          Next
        </button>
      </div>

      <div class="flex items-center gap-4 text-xs font-semibold text-gray-700">
        <div class="flex items-center gap-2">
          <span>Show:</span>
          <select
            v-model="size"
            class="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
            @change="handleSearch"
          >
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <span>
          Showing {{ (currentPage - 1) * size + 1 }} - {{ Math.min(currentPage * size, pagination.totalElements) }} of {{ pagination.totalElements }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { api } from "@/app/services/api.service";
import { useToast, useUtilities } from "@/composables";
import AppModal from "@/components/ui/AppModal.vue";
import AppInput from "@/components/ui/AppInput.vue";
import AppSelect from "@/components/ui/AppSelect.vue";
import AppSpinner from "@/components/ui/AppSpinner.vue";

// Interfaces
interface Log {
  id: string;
  service: string;
  userId: string;
  ip: string;
  method: string;
  statusCode: string | number;
  startTimestamp: string;
  endTimestamp: string;
  createdAt: string;
  url: string;
  queryString?: string;
  referer?: string;
  sessionId?: string;
  userAgent?: string;
  headers?: string;
  request?: string;
  response?: string;
}

interface Pagination {
  totalElements: number;
  totalPages: number;
}

interface Option {
  value: string | number;
  text: string;
}

// Composables
const route = useRoute();
const { warningToast, errorToast } = useToast();
const { dateFormat, objectToQueryParams } = useUtilities();

// Defaults helpers
function getDefaultYear(): number {
  return new Date().getFullYear();
}

function getDefaultMonth(): string {
  return String(new Date().getMonth() + 1).padStart(2, "0");
}

function buildYearOptions(): Option[] {
  const currentYear = new Date().getFullYear();
  const options: Option[] = [];
  for (let y = 2022; y <= currentYear; y++) {
    options.push({ value: y, text: String(y) });
  }
  return options.reverse();
}

function buildMonthOptions(): Option[] {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return months.map((name, i) => {
    const value = String(i + 1).padStart(2, "0");
    return { value, text: name };
  });
}

function toBackendDateTime(localValue: string): string | null {
  if (!localValue) return null;
  return localValue.replace("T", " ").substring(0, 16);
}

// Option Lists
const HTTP_STATUS_OPTIONS: Option[] = [
  { value: "", text: "All" },
  { value: "200", text: "200 OK" },
  { value: "201", text: "201 Created" },
  { value: "204", text: "204 No Content" },
  { value: "301", text: "301 Moved Permanently" },
  { value: "302", text: "302 Found" },
  { value: "304", text: "304 Not Modified" },
  { value: "400", text: "400 Bad Request" },
  { value: "401", text: "401 Unauthorized" },
  { value: "403", text: "403 Forbidden" },
  { value: "404", text: "404 Not Found" },
  { value: "405", text: "405 Method Not Allowed" },
  { value: "408", text: "408 Request Timeout" },
  { value: "409", text: "409 Conflict" },
  { value: "500", text: "500 Internal Server Error" },
  { value: "502", text: "502 Bad Gateway" },
  { value: "503", text: "503 Service Unavailable" }
];

const HTTP_METHOD_OPTIONS: Option[] = [
  { value: "", text: "All" },
  { value: "GET", text: "GET" },
  { value: "POST", text: "POST" },
  { value: "PUT", text: "PUT" },
  { value: "PATCH", text: "PATCH" },
  { value: "DELETE", text: "DELETE" },
  { value: "HEAD", text: "HEAD" },
  { value: "OPTIONS", text: "OPTIONS" }
];

// Reactive State
const loading = ref(false);
const exporting = ref(false);
const currentPage = ref(1);
const size = ref(20);
const logs = ref<Log[]>([]);
const pagination = ref<Pagination>({
  totalElements: 0,
  totalPages: 0
});

const filters = ref({
  year: getDefaultYear(),
  month: getDefaultMonth(),
  service: "",
  method: "",
  statusCode: "",
  userId: "",
  search: "",
  matchPhrase: true,
  startDateLocal: "",
  endDateLocal: ""
});

const selectedLog = ref<Log | null>(null);
const showDetailsModal = ref(false);

const yearOptions = ref<Option[]>(buildYearOptions());
const monthOptions = ref<Option[]>(buildMonthOptions());
const statusCodeOptions = ref<Option[]>(HTTP_STATUS_OPTIONS);
const methodOptions = ref<Option[]>(HTTP_METHOD_OPTIONS);
const serviceOptions = ref<Option[]>([{ value: "", text: "All" }]);
const knownServices = ref<string[]>([]);

// Computed State
const monthParam = computed(() => {
  if (filters.value.year && filters.value.month) {
    return filters.value.year + "." + filters.value.month;
  }
  return null;
});

// Methods
const handleSearch = () => {
  currentPage.value = 1;
  fetch();
};

const clearSearch = () => {
  filters.value.search = "";
  handleSearch();
};

const getFilterParams = (): Record<string, any> => {
  const p: Record<string, any> = {
    month: monthParam.value
  };
  if (filters.value.service) p.service = filters.value.service;
  if (filters.value.method) p.method = filters.value.method;
  if (filters.value.statusCode) p.statusCode = filters.value.statusCode;
  if (filters.value.userId) p.userId = filters.value.userId;
  if (filters.value.search && filters.value.search.trim()) {
    p.search = filters.value.search.trim();
    p.matchPhrase = filters.value.matchPhrase;
  }

  const startDate = toBackendDateTime(filters.value.startDateLocal);
  const endDate = toBackendDateTime(filters.value.endDateLocal);
  if (startDate) p.starDate = startDate; // Typo 'starDate' matches backend API expectation
  if (endDate) p.endDate = endDate;

  return p;
};

const buildQueryString = (): string => {
  const p = getFilterParams();
  p.page = currentPage.value;
  p.size = size.value;

  return objectToQueryParams(p);
};

const parseFilename = (contentDisposition: string | undefined, fallback: string): string => {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (match?.[1]) {
    return match[1].replace(/['"]/g, "").trim();
  }
  return fallback;
};

const exportLogs = () => {
  if (!monthParam.value) {
    warningToast("Validation", "Please select Year and Month");
    return;
  }
  exporting.value = true;
  const query = objectToQueryParams(getFilterParams());
  api()
    .get("activity-logs/request-logs/export?" + query, { responseType: "blob" })
    .then((response) => {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream"
      });
      const fallbackName = `request-logs-${filters.value.year}-${filters.value.month}.xlsx`;
      const filename = parseFilename(response.headers["content-disposition"], fallbackName);

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      const msg =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to export activity logs";
      errorToast("Error", msg);
    })
    .finally(() => {
      exporting.value = false;
    });
};

const updateServiceOptionsFromLogs = (newLogs: Log[]) => {
  const list = [...knownServices.value];
  newLogs.forEach((log) => {
    const s = log?.service;
    if (s && typeof s === "string" && !list.includes(s)) {
      list.push(s);
    }
  });
  list.sort();
  knownServices.value = list;
  serviceOptions.value = [{ value: "", text: "All" }].concat(
    list.map((s) => ({ value: s, text: s }))
  );
};

const fetch = () => {
  if (!monthParam.value) {
    warningToast("Validation", "Please select Year and Month");
    return;
  }
  loading.value = true;
  const query = buildQueryString();
  api()
    .get("activity-logs/request-logs?" + query)
    .then((response) => {
      const data = response.data;
      const totalElements = data.totalElements != null ? data.totalElements : (data.total != null ? data.total : 0);
      const totalPages = data.totalPages != null ? data.totalPages : (Math.ceil(totalElements / size.value) || 1);
      
      let raw = data.content || data.data || data.requestLogs;
      if (raw && typeof raw === "object" && !Array.isArray(raw)) {
        raw = Object.keys(raw).map((k) => (raw as any)[k]);
      }
      if (!Array.isArray(raw) && data && typeof data === "object") {
        const listKeys = Object.keys(data).filter(
          (k) => k !== "totalElements" && k !== "totalPages" && k !== "total" && /^\d+$/.test(k)
        );
        if (listKeys.length) {
          raw = listKeys.map((k) => (data as any)[k]);
        }
      }
      
      logs.value = Array.isArray(raw) ? raw : [];
      pagination.value = {
        totalElements,
        totalPages
      };
      updateServiceOptionsFromLogs(logs.value);
    })
    .catch((error) => {
      logs.value = [];
      pagination.value = { totalElements: 0, totalPages: 0 };
      const msg =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        "Failed to load activity logs";
      errorToast("Error", msg);
    })
    .finally(() => {
      loading.value = false;
    });
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetch();
  } else {
    warningToast("Observation", "No more data to show");
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  } else {
    warningToast("Observation", "No more data to show");
  }
};

const statusCodeClass = (code: string | number | undefined): string => {
  if (!code) return "text-gray-500 border-gray-300";
  const n = typeof code === "string" ? parseInt(code, 10) : code;
  if (n >= 200 && n < 300) return "text-success border-success-200 bg-success-50";
  if (n >= 400 && n < 500) return "text-warning-600 border-warning-200 bg-warning-50";
  if (n >= 500) return "text-danger border-danger-200 bg-danger-50";
  return "text-gray-500 border-gray-300 bg-gray-50";
};

const viewDetails = (log: Log) => {
  selectedLog.value = log;
  showDetailsModal.value = true;
};

// Lifecycle
onMounted(() => {
  if (route?.query?.search && typeof route.query.search === "string") {
    filters.value.search = route.query.search;
  }
  fetch();
});
</script>
